import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, CheckIcon, Loader2Icon, MailCheckIcon } from 'lucide-react';
import { AuthLayout } from '../../layouts/AuthLayout';
import { useAuth } from '../../contexts/AuthContext';
import { updateOrganization } from '../../services/organizationService';
import { SECTOR_OPTIONS } from '../../data/sectors';
import { BrandLogo } from '../../components/shared/BrandLogo';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  repeatPassword: string;
  companyName: string;
  sector: string;
  primaryColor: string;
  secondaryColor: string;
}
const INITIAL: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  repeatPassword: '',
  companyName: '',
  sector: '',
  primaryColor: '#3D8BFF',
  secondaryColor: '#0B1F4D'
};
const STEPS = ['Tu cuenta', 'Tu empresa', 'Logotipo', 'Colores', 'Sector'];
const CREATING_STEPS = ['Creando tu cuenta', 'Creando el espacio de trabajo de tu empresa', 'Preparando los estados iniciales del pipeline'];
const PRESET_COLORS = ['#3D8BFF', '#0B1F4D', '#16A34A', '#7C3AED', '#F97316', '#0EA5E9'];
export function Register() {
  const {
    register,
    loading
  } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState<FormState>(INITIAL);
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  /** La cuenta se ha creado pero necesita confirmarse por email. */
  const [confirmEmail, setConfirmEmail] = useState(false);
  function set(key: keyof FormState, value: string) {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: ''
    }));
  }
  function validateStep(index: number): boolean {
    const next: Record<string, string> = {};
    if (index === 0) {
      if (!values.firstName.trim()) next.firstName = 'Introduce tu nombre.';
      if (!values.lastName.trim()) next.lastName = 'Introduce tus apellidos.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) next.email = 'Introduce un email válido.';
      if (values.password.length < 8) next.password = 'Usa al menos 8 caracteres.';
      if (values.password !== values.repeatPassword) next.repeatPassword = 'Las contraseñas no coinciden.';
      if (!terms) next.terms = 'Debes aceptar los términos.';
    }
    if (index === 1 && !values.companyName.trim()) next.companyName = 'Introduce el nombre de tu empresa.';
    if (index === 4 && !values.sector) next.sector = 'Selecciona un sector.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  async function finish() {
    if (!validateStep(4)) return;
    setCreating(true);
    try {
      const session = await register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        companyName: values.companyName,
        sector: values.sector,
        primaryColor: values.primaryColor,
        secondaryColor: values.secondaryColor,
        logoUrl: logo
      });

      // Sin sesión: la cuenta debe confirmarse por email y el espacio de
      // trabajo se creará en el primer acceso.
      if (!session) {
        setCreating(false);
        setConfirmEmail(true);
        return;
      }
      await updateOrganization(session.organizationId, {
        logoUrl: logo,
        brand: {
          primaryColor: values.primaryColor,
          secondaryColor: values.secondaryColor,
          fontFamily: 'Inter',
          logoUrl: logo
        }
      });
      navigate('/app/dashboard');
    } catch (err) {
      setCreating(false);
      setStep(0);
      setErrors({
        email: err instanceof Error ? err.message : 'No se ha podido crear la cuenta.'
      });
    }
  }
  if (confirmEmail) {
    return <AuthLayout title="Confirma tu correo" subtitle={`Hemos enviado un enlace de confirmación a ${values.email}.`} footer={<span className="text-muted-foreground" data-id="element-1418" data-mp="17ik5e6cnx5uh">
            ¿Ya lo has confirmado?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline" data-id="element-1419" data-mp="1mf1e0ptgw6dc">
              Iniciar sesión
            </Link>
          </span>} data-id="element-1417" data-mp="yo5ush32ujhp">
        <div className="space-y-5 text-sm leading-relaxed text-muted-foreground" data-id="element-1420" data-mp="1hh000jawex8q">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10" data-id="element-1421" data-mp="3o77senls3hv8">
            <MailCheckIcon className="h-6 w-6 text-primary" aria-hidden="true" data-id="element-1422" data-mp="3j960dd6dk87m" />
          </span>
          <p data-id="element-1423" data-mp="1yq2j17zzl0ze">
            Abre el enlace del correo para activar tu cuenta. Al iniciar sesión
            por primera vez crearemos automáticamente el espacio de trabajo de{' '}
            <strong className="text-foreground" data-id="element-1424" data-mp="1oypqsfaa08su">{values.companyName}</strong> con
            tu logotipo, tus colores, los estados del embudo y tu primera landing.
          </p>
          <p data-id="element-1425" data-mp="1fyjgf29vjoxw">
            Si no lo encuentras, revisa la carpeta de spam. El enlace caduca en
            24 horas.
          </p>
          <Link to="/login" data-id="element-1426" data-mp="1k1fsea4wsmsv">
            <Button size="lg" className="w-full" data-id="element-1427" data-mp="1jyjswhdexq2b">
              Ir a iniciar sesión
            </Button>
          </Link>
        </div>
      </AuthLayout>;
  }
  if (creating) {
    return <AuthLayout title="Estamos creando tu CRM" subtitle={`Espacio de trabajo de ${values.companyName}`} data-id="element-1428" data-mp="1b0866qfo3cf5">
        <ul className="space-y-4" data-id="element-1429" data-mp="35dzffou33ua2">
          {CREATING_STEPS.map((label, index) => <li key={label} className="flex items-center gap-3 text-sm" data-id="element-1430" data-mp="35bmud2t153ss" data-mpi={index}>
              {index === 0 ? <CheckCircle2Icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-1431" data-mp="2yxqtejushhgs" /> : <Loader2Icon className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" data-id="element-1432" data-mp="2tye5rww3g6d5" />}
              <span className="text-foreground" data-id="element-1433" data-mp="2p1nox7cgt6qo">{label}</span>
            </li>)}
        </ul>
      </AuthLayout>;
  }
  const stepper = <ol className="mb-9 flex items-center gap-2" aria-label="Progreso del registro" data-id="element-1434" data-mp="3hnyu6vb0nvtt">
      {STEPS.map((label, index) => <li key={label} className="flex flex-1 items-center gap-2" data-id="element-1435" data-mp="3cvxknh58gj7h" data-mpi={index}>
          <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-full text-[11px] font-semibold transition-colors ${index < step ? 'bg-primary/10 text-primary' : index === step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`} data-id="element-1436" data-mp="37z72mo9vbdjf">
            {index < step ? <CheckIcon className="h-3.5 w-3.5" data-id="element-1437" data-mp="gszi00ff4407" /> : index + 1}
          </span>
          {index < STEPS.length - 1 && <span className={`h-px flex-1 ${index < step ? 'bg-primary/40' : 'bg-border'}`} aria-hidden="true" data-id="element-1438" data-mp="lpptsbk6wdft" />}
        </li>)}
    </ol>;
  return <AuthLayout title={step === 0 ? 'Crea el CRM de tu empresa' : `Paso ${step + 1} · ${STEPS[step]}`} subtitle={step === 0 ? 'Tu espacio de trabajo se crea automáticamente y solo tu equipo podrá verlo.' : 'Personaliza el espacio de trabajo de tu empresa. Podrás cambiarlo cuando quieras.'} footer={step === 0 ? <span className="text-muted-foreground" data-id="element-1440" data-mp="1ehj79v8v6ktb">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline" data-id="element-1441" data-mp="1jfayen1oq2wg">
              Iniciar sesión
            </Link>
          </span> : undefined} data-id="element-1439" data-mp="voymz278do14">
      {stepper}

      <div className="space-y-5" data-id="element-1442" data-mp="1zq514ub9o0mh">
        {step === 0 && <>
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1443" data-mp="2dpd3fw2q7ouw">
              <Input id="firstName" label="Nombre" value={values.firstName} error={errors.firstName} onChange={e => set('firstName', e.target.value)} data-id="element-1444" data-mp="28rbmphsrw1ml" />
              <Input id="lastName" label="Apellidos" value={values.lastName} error={errors.lastName} onChange={e => set('lastName', e.target.value)} data-id="element-1445" data-mp="1h1gkwlnqajh1" />
            </div>
            <Input id="email" label="Email" type="email" autoComplete="email" value={values.email} error={errors.email} onChange={e => set('email', e.target.value)} data-id="element-1446" data-mp="12azmlb6e49od" />
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1447" data-mp="38xorcoakl0a8">
              <Input id="password" label="Contraseña" type="password" autoComplete="new-password" value={values.password} error={errors.password} onChange={e => set('password', e.target.value)} data-id="element-1448" data-mp="3dr1ib52q1j65" />
              <Input id="repeatPassword" label="Repetir contraseña" type="password" autoComplete="new-password" value={values.repeatPassword} error={errors.repeatPassword} onChange={e => set('repeatPassword', e.target.value)} data-id="element-1449" data-mp="3soaf4easnf59" />
            </div>
            <div data-id="element-1450" data-mp="279ecuu78wbep">
              <Checkbox id="terms" label="Acepto los términos y la política de privacidad." checked={terms} onChange={e => {
            setTerms(e.target.checked);
            setErrors(prev => ({
              ...prev,
              terms: ''
            }));
          }} data-id="element-1451" data-mp="278us75q7n8wz" />
              {errors.terms && <p role="alert" className="mt-2 text-sm font-medium text-red-600" data-id="element-1452" data-mp="2d8jaqyxkczr5">
                  {errors.terms}
                </p>}
            </div>
          </>}

        {step === 1 && <Input id="companyName" label="Nombre de la empresa" placeholder="Mi Empresa S.L." value={values.companyName} error={errors.companyName} onChange={e => set('companyName', e.target.value)} data-id="element-1453" data-mp="247bc2fr4xwwj" />}

        {step === 2 && <>
            <LogoUploader value={logo} onChange={setLogo} data-id="element-1454" data-mp="1zbmhyrxvx87m" />
            <div className="rounded-xl border border-border bg-surface p-4" data-id="element-1455" data-mp="3oq120yxyk6ws">
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground" data-id="element-1456" data-mp="3to2kpirh247q">
                Vista previa
              </p>
              <div className="flex items-center gap-3 rounded-lg bg-background px-4 py-3" data-id="element-1457" data-mp="1hycva9jy0xyr">
                <BrandLogo src={logo} size={22} data-id="element-1458" data-mp="1cz9s6unix1tf" />
                <span className="text-sm text-muted-foreground" data-id="element-1459" data-mp="1rqio1li0rjf0">
                  {values.companyName || 'Tu empresa'}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground" data-id="element-1460" data-mp="20qoxmwaljz8w">
              Puedes continuar sin logotipo y añadirlo más adelante desde
              Configuración → Identidad de empresa.
            </p>
          </>}

        {step === 3 && <>
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-1461" data-mp="2aod4gklh8x7f">
              <Input id="primary" label="Color principal" type="color" value={values.primaryColor} onChange={e => set('primaryColor', e.target.value)} data-id="element-1462" data-mp="25ps66qpscnlm" />
              <Input id="secondary" label="Color secundario" type="color" value={values.secondaryColor} onChange={e => set('secondaryColor', e.target.value)} data-id="element-1463" data-mp="be3oigs666xj" />
            </div>
            <div data-id="element-1464" data-mp="1a02sy45qteog">
              <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1465" data-mp="19zjg31uy9gha">Sugerencias</p>
              <div className="flex flex-wrap gap-2" data-id="element-1466" data-mp="1f1igc2ve7xa0">
                {PRESET_COLORS.map(color => <button key={color} type="button" aria-label={`Usar ${color} como color principal`} onClick={() => set('primaryColor', color)} style={{
              backgroundColor: color
            }} className={`h-9 w-9 rounded-full border-2 transition-transform hover:scale-105 ${values.primaryColor === color ? 'border-foreground' : 'border-transparent'}`} data-id="element-1467" data-mp="1jys3tcz81yv7" />)}
              </div>
            </div>
            <div className="rounded-xl border border-border p-5" style={{
          borderColor: `${values.primaryColor}33`
        }} data-id="element-1468" data-mp="3s4atfqtvt40i">
              <p className="text-xs uppercase tracking-wider text-muted-foreground" data-id="element-1469" data-mp="3i993isrs7xwv">
                Vista previa
              </p>
              <p className="mt-2 text-lg font-semibold" style={{
            color: values.secondaryColor
          }} data-id="element-1470" data-mp="3dbr35qrtyr0h">
                {values.companyName || 'Tu empresa'}
              </p>
              <span className="mt-4 inline-flex h-10 items-center rounded-lg px-5 text-sm font-medium text-white" style={{
            backgroundColor: values.primaryColor
          }} data-id="element-1471" data-mp="51xveghbhy7i">
                Botón principal
              </span>
            </div>
          </>}

        {step === 4 && <Select id="sector" label="Sector de la empresa" placeholder="Selecciona un sector" options={SECTOR_OPTIONS} value={values.sector} error={errors.sector} onChange={e => set('sector', e.target.value)} data-id="element-1472" data-mp="sra65ksamebx" />}
      </div>

      <div className="mt-9 flex items-center justify-between gap-3" data-id="element-1473" data-mp="3cvzwnb1buin">
        <Button variant="ghost" disabled={step === 0} leftIcon={<ArrowLeftIcon className="h-4 w-4" data-id="element-1475" data-mp="vzgqvmnlm4lo" />} onClick={() => setStep(s => Math.max(0, s - 1))} data-id="element-1474" data-mp="89mgrcunyu54">
          Anterior
        </Button>
        {step < STEPS.length - 1 ? <Button size="lg" rightIcon={<ArrowRightIcon className="h-4 w-4" data-id="element-1477" data-mp="m9x5a98i3wn4" />} onClick={() => {
        if (validateStep(step)) setStep(s => s + 1);
      }} data-id="element-1476" data-mp="magrw393j97h">
            Continuar
          </Button> : <Button size="lg" className="ava-glow" loading={loading} onClick={finish} data-id="element-1478" data-mp="1ex1fqtqd536a">
            Crear mi CRM
          </Button>}
      </div>
    </AuthLayout>;
}